import {
  constructLocaleFromLangAndCountryCode,
  getCountryCodeFromLocale,
} from "@/apiUtils/commonUtils";
import { localeConstants } from "@/apiUtils/constants";
import { locales } from "@/runtimeConfig/public";

export const POS_LANG_MAP: Record<number, string> = { 1: "en", 2: "ar" };

/** Maps a POS `preferedLanguageId` (1 = en, 2 = ar) to a language code; `undefined` when unknown. */
export const mapPosLanguageIdToLang = (preferedLanguageId?: number | null) =>
  preferedLanguageId == null ? undefined : POS_LANG_MAP[preferedLanguageId];

/** Builds the storefront locale for `lang` ("en" | "ar"), keeping the country of `currentLocale`; `undefined` for unknown languages. */
export const resolveLocaleFromLang = (lang: string | null | undefined, currentLocale: string): string | undefined => {
  const normalizedLang = lang?.toLowerCase();
  if (!normalizedLang || !Object.values(POS_LANG_MAP).includes(normalizedLang)) return undefined;
  return constructLocaleFromLangAndCountryCode(normalizedLang, getCountryCodeFromLocale(currentLocale));
};

/**
 * Builds the storefront locale for a POS customer's preferred language, keeping the country of `currentLocale`.
 * Returns `undefined` when the customer has no recognised preferred language.
 */
export const resolveLocaleFromPosCustomer = (
  customer: { preferedLanguageId?: number; preferedLangId?: number } | null | undefined,
  currentLocale: string,
): string | undefined =>
  resolveLocaleFromLang(mapPosLanguageIdToLang(customer?.preferedLanguageId ?? customer?.preferedLangId), currentLocale);

/** Replaces (or prepends, if missing) the locale segment of `url`'s path with `newLocale`; returns `url` unchanged if it can't be parsed. */
export const rewriteUrlWithLocale = (url: string, newLocale: string): string => {
  try {
    const parsed = new URL(url);
    const localePattern = new RegExp(
      `^/(${locales.map((l) => l.replace(/-/g, "\\-")).join("|")})(/|$)`,
    );
    if (localePattern.test(parsed.pathname)) {
      parsed.pathname = parsed.pathname.replace(localePattern, `/${newLocale}$2`);
    } else {
      parsed.pathname = `/${newLocale}${parsed.pathname === "/" ? "" : parsed.pathname}`;
    }
    return parsed.toString();
  } catch {
    return url;
  }
};

/** Persists `locale` in the `NEXT_LOCALE` cookie so the next navigation renders in that locale. No-op on the server. */
export const setClientLocaleCookie = (locale: string) => {
  if (typeof document === "undefined") return;
  document.cookie = `${localeConstants.NEXT_LOCALE}=${locale}; path=/; SameSite=Lax`;
};
